import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from 'react'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { useUser } from './session-provider'
import { createClient } from '@/lib/supabase/client'

interface Firm {
  id: string
  name: string
  role: string
}

interface FirmContextType {
  firms: Firm[]
  activeFirm: Firm | null
  setActiveFirmId: (id: string) => void
  loading: boolean
}

const FirmContext = createContext<FirmContextType>({
  firms: [],
  activeFirm: null,
  setActiveFirmId: () => {},
  loading: true,
})

export function useFirm() {
  return useContext(FirmContext)
}

const STORAGE_KEY = 'shoppanel-active-firm-id'

// Cookie name must match packages/api/src/lib/firm-cookie.ts FIRM_COOKIE_NAME.
// The cookie is HMAC-signed server-side and scoped to .emonse.com so the
// shoppanel-app SSR loader + api.emonse.com both read it. Format:
// `<firmId>.<base64url-hmac>`. Client only reads the firmId prefix — we
// never verify the HMAC on the client (server does that on every request).
// Cookie-Krieg-Fix 19.07.2026: Demo-Instanz nutzt 'sp-firm-demo'
// (Inline-Env-Check wie session-provider.tsx). Prod/Preview unverändert.
const FIRM_COOKIE_NAME = import.meta.env.VITE_DEMO_MODE === 'true' ? 'sp-firm-demo' : 'sp-firm'

function readFirmIdFromCookie(): string | null {
  if (typeof document === 'undefined') return null
  for (const part of document.cookie.split(';')) {
    const trimmed = part.trim()
    const eq = trimmed.indexOf('=')
    if (eq <= 0) continue
    if (trimmed.slice(0, eq) !== FIRM_COOKIE_NAME) continue
    const value = decodeURIComponent(trimmed.slice(eq + 1))
    const dotIdx = value.lastIndexOf('.')
    return dotIdx > 0 ? value.slice(0, dotIdx) : null
  }
  return null
}

// 07.07.2026 (User): Deep-Link-Firmen-Override. Telegram/Preis-Alarm-Links tragen
// ?firm=<firmId> (siehe telegram-price-alert.ts), damit ein Klick auf z.B. einen
// Emonse-Alarm das Panel auf DIESE Firma schaltet statt die zuletzt aktive (Bifamo)
// zu zeigen — sonst wirken die "Bekleyen Onaylar" widersprüchlich zum Alarm. Reiner
// View-State: der Param wird nur akzeptiert, wenn die id in der eigenen firmList des
// Users liegt; jeder /api-Call autorisiert weiterhin server-seitig über user.firms.
function readFirmIdFromUrl(): string | null {
  if (typeof window === 'undefined') return null
  const v = new URLSearchParams(window.location.search).get('firm')
  return v && v.trim() ? v.trim() : null
}

/**
 * Tell the API to set the signed firm-id cookie. Best-effort — if the
 * request fails we still update local state so the user isn't blocked.
 * SSR prefetch will just fall back to firms[0] on the next page load
 * until the cookie is set.
 *
 * Concurrency: a user can fire multiple selections in quick succession
 * (click firm A, then B). Fire-and-forget without ordering = a stale
 * response can overwrite the newer cookie (Codex HIGH finding). Fix:
 * a module-scoped AbortController cancels the previous in-flight call
 * before starting the next, and a monotonic sequence number tells
 * callers whether their result is still the current one.
 */
let selectAbort: AbortController | null = null
let selectSeq = 0
async function persistFirmSelection(firmId: string): Promise<void> {
  // Cancel the previous /select if any — the user's newer intent wins.
  if (selectAbort) {
    try {
      selectAbort.abort()
    } catch {
      /* already-aborted is fine */
    }
  }
  const controller = new AbortController()
  selectAbort = controller
  const mySeq = ++selectSeq
  try {
    const supabase = createClient()
    const { data: sessionData } = await supabase.auth.getSession()
    const token = sessionData?.session?.access_token
    const res = await fetch('/api/firms/select', {
      method: 'POST',
      credentials: 'include',
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ firmId }),
    })
    // If another /select fired after us, its response is now authoritative.
    // Discard our result even on success so we don't re-trigger SSR logic
    // based on a superseded firm.
    if (mySeq !== selectSeq) return
    if (!res.ok) {
      console.warn('[firm-provider] persistFirmSelection non-ok:', res.status)
    }
  } catch (err) {
    // AbortError on supersession is expected; swallow.
    if (err instanceof DOMException && err.name === 'AbortError') return
    console.warn('[firm-provider] persistFirmSelection failed:', err)
  } finally {
    if (selectAbort === controller) selectAbort = null
  }
}

export function FirmProvider({ children }: { children: ReactNode }) {
  const { user } = useUser()
  const navigate = useNavigate()
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const [firms, setFirms] = useState<Firm[]>([])
  const [activeFirmId, setActiveFirmIdState] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const hasFetched = useRef(false)

  // Load firms ONCE when user is authenticated
  useEffect(() => {
    if (!user) {
      setFirms([])
      setLoading(false)
      hasFetched.current = false
      return
    }

    if (hasFetched.current) return
    hasFetched.current = true

    const loadFirms = async () => {
      try {
        const supabase = createClient()
        const { data: sessionData } = await supabase.auth.getSession()
        const token = sessionData?.session?.access_token

        const fetchFirms = () =>
          fetch('/api/firms', {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          })
        let res: Response
        try {
          res = await fetchFirms()
          if (!res.ok) {
            // Fehlerliste 21.07 Sidebar-Erstrender-Retry: ein Fehlabruf liess die Sidebar bis zum Reload leer.
            await new Promise((resolve) => window.setTimeout(resolve, 2_000))
            res = await fetchFirms()
          }
        } catch {
          await new Promise((resolve) => window.setTimeout(resolve, 2_000))
          res = await fetchFirms()
        }

        if (!res.ok) {
          setLoading(false)
          return
        }

        // as-Cast statt r.json<T>(): packages/ui kompiliert ohne Worker-Typen,
        // dort nimmt json() gar kein Typargument (siehe admin-entry-button.tsx).
        const data = (await res.json()) as { firms?: Firm[] }
        const firmList = data.firms || []
        setFirms(firmList)

        if (firmList.length === 0 && pathname !== '/onboarding') {
          navigate({ to: '/onboarding' })
          setLoading(false)
          return
        }

        // Resolve active firm — priority order:
        //   1. Signed cookie (server-readable for SSR prefetch)
        //   2. localStorage (backward-compat with pre-cookie users)
        //   3. First firm (default)
        const urlFirmId = readFirmIdFromUrl()
        const cookieFirmId = readFirmIdFromCookie()
        const stored = localStorage.getItem(STORAGE_KEY)
        const fromUrl = urlFirmId ? firmList.find((f: Firm) => f.id === urlFirmId) : undefined
        const fromCookie = firmList.find((f: Firm) => f.id === cookieFirmId)
        const fromStored = firmList.find((f: Firm) => f.id === stored)
        if (fromUrl) {
          // Deep-Link gewinnt vor Cookie/localStorage: aktive Firma auf die verlinkte
          // umschalten und persistieren, damit SSR-Cookie + alter localStorage-Pfad bei
          // Folge-Loads übereinstimmen. Unbekannter/ fremder Slug → fromUrl undefined →
          // fällt unverändert auf die bestehende Cookie→localStorage→firms[0]-Kette.
          setActiveFirmIdState(fromUrl.id)
          try {
            localStorage.setItem(STORAGE_KEY, fromUrl.id)
          } catch {
            /* quota etc. */
          }
          void persistFirmSelection(fromUrl.id)
        } else if (fromCookie) {
          setActiveFirmIdState(cookieFirmId!)
          // Mirror to localStorage so the old read path stays valid during
          // transition. Also: if the user was on old localStorage-only
          // code and the cookie is new, prefer cookie as truth.
          try {
            localStorage.setItem(STORAGE_KEY, cookieFirmId!)
          } catch {
            /* quota etc. */
          }
        } else if (fromStored) {
          setActiveFirmIdState(stored!)
          // Bootstrap: user has firmId in localStorage but no cookie yet.
          // Fire-and-forget a /select call so future SSR loads have it.
          void persistFirmSelection(stored!)
        } else if (firmList.length > 0) {
          const defaultId = firmList[0].id
          setActiveFirmIdState(defaultId)
          void persistFirmSelection(defaultId)
        }
        setLoading(false)
      } catch {
        setLoading(false)
      }
    }

    loadFirms()
  }, [user, pathname, navigate])

  const setActiveFirmId = useCallback((id: string) => {
    setActiveFirmIdState(id)
    localStorage.setItem(STORAGE_KEY, id)
    // Fire-and-forget — cookie write should not block the UI.
    // Any 403 (firm access denied) is a bug elsewhere; the user's
    // own firm list is already validated at render.
    void persistFirmSelection(id)
  }, [])

  const activeFirm = useMemo(
    () => firms.find((f) => f.id === activeFirmId) || null,
    [firms, activeFirmId],
  )

  const value = useMemo(
    () => ({ firms, activeFirm, setActiveFirmId, loading }),
    [firms, activeFirm, setActiveFirmId, loading],
  )

  return <FirmContext.Provider value={value}>{children}</FirmContext.Provider>
}
